import React, { useMemo, useState, useRef, useCallback } from 'react';
import { t } from 'i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DataTable from '../util/DataTable';
import { ColDef } from '../util/DataTable';
import PageToolbar from '../util/PageToolbar';
import SearchFilter, { applySearchAndFilters, ActiveFilter } from '../util/SearchFilter';
import SidePanel from '../util/SidePanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown, faFileCsv, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import UserAvatar from '../util/UserAvatar';

interface FinanceOverviewProps {
  expenses: any[];
  income: any[];
  carExpenses: any[];
  loading: boolean;
  baseUrl?: string;
  access?: any;
}

const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  pending:   { color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  rejected:  { color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  fulfilled: { color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
  paid:      { color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
};

const TYPE_COLORS: Record<string, { color: string; bg: string }> = {
  Income:       { color: 'var(--color-success)',  bg: 'var(--color-success-bg)' },
  Expense:      { color: 'var(--color-danger)',   bg: 'var(--color-danger-bg)' },
  'Car Expense':{ color: '#7c3aed',               bg: 'rgba(124,58,237,.1)' },
};

const StatCard: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <div className="bg-[var(--bg-form)] rounded-[5px] px-[20px] py-[14px] flex flex-col gap-[4px] flex-1 min-w-[150px]">
    <p className="text-[12px] font-semibold text-[var(--text-color-calendar-week)]">{label}</p>
    <p className="text-[22px] font-semibold" style={{ color: color || 'var(--text-color)' }}>{value}</p>
  </div>
);


function exportCsv(rows: any[]) {
  const headers = ['Date', 'Type', 'Description', 'Creator', 'Category', 'Amount', 'Status'];
  const lines = rows.map(r => [
    r.date ? new Date(r.date).toLocaleDateString() : '',
    r._type || '',
    (r.description || r.purpose || '').replace(/"/g, '""'),
    r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '',
    (r.categoryTree || r.category?.name || '').replace(/"/g, '""'),
    Number(r._amount || 0).toFixed(2),
    r.status || '',
  ].map(v => `"${v}"`).join(','));
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `finance-export-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const FinanceOverview: React.FC<FinanceOverviewProps> = ({
  expenses, income, carExpenses, loading, baseUrl = '', access,
}) => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ActiveFilter[]>([]);
  const [attachUrl, setAttachUrl] = useState<string | null>(null);
  const [attachName, setAttachName] = useState('');
  const [zipping, setZipping] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const toolbarSlot = useRef<HTMLDivElement>(null);

  const stats = useMemo(() => {
    const sumExpenses  = expenses.filter(r => r.status !== 'rejected').reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const sumCar       = carExpenses.filter(r => r.status !== 'rejected').reduce((s, r) => s + (Number(r.cost) || 0), 0);
    const sumIncome    = income.filter(r => r.status !== 'rejected').reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const outgoing     = sumExpenses + sumCar;
    const balance      = sumIncome - outgoing;
    const transactions = expenses.length + income.length + carExpenses.length;
    return { balance, incoming: sumIncome, outgoing, transactions };
  }, [expenses, income, carExpenses]);

  const allTransactions = useMemo(() => [
    ...income.map(r => ({ ...r, _type: 'Income',       _amount: r.amount, _file: null })),
    ...expenses.map(r => ({ ...r, _type: 'Expense',    _amount: r.amount, _file: r.payment_document_url || null })),
    ...carExpenses.map(r => ({ ...r, _type: 'Car Expense', _amount: r.cost, _file: null })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
  [expenses, income, carExpenses]);

  const userOptions = useMemo(() => {
    const seen = new Set<string>();
    return allTransactions.reduce((acc: { value: string; label: string }[], r) => {
      if (r.create_user) {
        const name = `${r.create_user.first_name} ${r.create_user.last_name}`;
        if (!seen.has(name)) { seen.add(name); acc.push({ value: name, label: name }); }
      }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [allTransactions]);

  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    return allTransactions.reduce((acc: { value: string; label: string }[], r) => {
      const cat = r.categoryTree || r.category?.name || '';
      if (cat && !seen.has(cat)) { seen.add(cat); acc.push({ value: cat, label: cat }); }
      return acc;
    }, []).sort((a, b) => a.label.localeCompare(b.label));
  }, [allTransactions]);

  const openAttachment = useCallback((r: any) => {
    const url = `${baseUrl}${(r._file || '').replace(/^\//, '')}`;
    setAttachName(r._file?.split('/').pop() || 'attachment');
    setAttachUrl(url);
  }, [baseUrl]);

  const handleExportZip = async () => {
    const ids = shownRows.filter((r: any) => r._type === 'Expense' && r._file).map((r: any) => r.id);
    if (!ids.length) return;
    setZipping(true);
    try {
      const resp = await axios.post('expenses/export-zip', { ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachments-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {} finally {
      setZipping(false);
    }
  };

  const columns = useMemo((): ColDef[] => [
    {
      key: 'date', label: t('Date'), sortable: true,
      filterType: 'daterange',
      filterExtract: (r) => r.date ?? '',
      render: (r) => r.date ? new Date(r.date).toLocaleDateString() : '—',
    },
    {
      key: '_type', label: t('Type'), filterType: 'select',
      filterExtract: (r) => r._type ?? '',
      filterOptions: [
        { value: 'Income',      label: 'Income' },
        { value: 'Expense',     label: 'Expense' },
        { value: 'Car Expense', label: 'Car Expense' },
      ],
      render: (r) => {
        const tc = TYPE_COLORS[r._type] || { color: 'var(--text-color)', bg: 'transparent' };
        return (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
            background: tc.bg, color: tc.color, whiteSpace: 'nowrap',
          }}>{r._type}</span>
        );
      },
    },
    {
      key: 'description', label: t('Description'),
      render: (r) => {
        const desc = r.description || r.purpose || '—';
        const cat  = r.categoryTree || r.category?.name || '';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 12, color: 'var(--text-color)' }}>{desc}</span>
            {cat && <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{cat}</span>}
          </div>
        );
      },
    },
    {
      key: 'create_user', label: t('NameCreator'),
      filterType: 'select',
      filterOptions: userOptions,
      filterExtract: (r) => r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '',
      render: (r) => r.create_user ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserAvatar user={r.create_user} baseUrl={baseUrl} size={28} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-color)', lineHeight: 1.2 }}>
              {r.create_user.first_name} {r.create_user.last_name}
            </span>
            {r.create_user.job_title && (
              <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', lineHeight: 1.2 }}>
                {r.create_user.job_title}
              </span>
            )}
          </div>
        </div>
      ) : <span style={{ color: 'var(--text-color-calendar-week)' }}>—</span>,
    },
    {
      key: 'categoryTree', label: t('Category'),
      filterType: 'select',
      filterOptions: categoryOptions,
      filterExtract: (r) => r.categoryTree || r.category?.name || '',
      defaultVisible: false,
      render: (r) => r.categoryTree || r.category?.name || '—',
    },
    {
      key: '_amount', label: t('Amount'), sortable: true, align: 'right',
      sortExtract: (r) => Number(r._amount) || 0,
      render: (r) => (
        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: r._type === 'Income' ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {r._type === 'Income' ? '+' : '−'}{Number(r._amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: 'status', label: t('StatusExpense'), filterType: 'select',
      filterExtract: (r) => r.status ?? '',
      filterOptions: [
        { value: 'pending',   label: 'Pending' },
        { value: 'fulfilled', label: 'Approved' },
        { value: 'paid',      label: 'Paid' },
        { value: 'rejected',  label: 'Rejected' },
      ],
      render: (r) => {
        if (!r.status) return <span style={{ color: 'var(--text-color-calendar-week)' }}>—</span>;
        const sc = STATUS_COLORS[r.status];
        return (
          <Chip
            label={r.status === 'fulfilled' ? 'approved' : r.status}
            variant="outlined"
            sx={{
              height: 26, fontSize: '0.72rem', fontWeight: 700,
              borderRadius: '20px',
              color: sc?.color ?? 'var(--text-color)',
              borderColor: sc?.color ?? 'var(--border-input)',
              background: sc?.bg ?? 'transparent',
              '& .MuiChip-label': { padding: '0 9px' },
            }}
          />
        );
      },
    },
    {
      key: '_file', label: '📎', sortable: false,
      filterType: 'boolean',
      filterExtract: (r) => r._file ? 'true' : 'false',
      render: (r) => r._file ? (
        <Tooltip title={r._file.split('/').pop()}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openAttachment(r); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
              color: '#3b82f6', fontSize: 14, display: 'flex', alignItems: 'center',
            }}
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </button>
        </Tooltip>
      ) : <span style={{ color: 'var(--text-color-calendar-week)', fontSize: 11 }}>—</span>,
    },
  ], [userOptions, categoryOptions, openAttachment, baseUrl]);

  // shownRows must be defined before handleExportZip uses it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shownRows = useMemo(
    () => applySearchAndFilters(allTransactions, search, filters, columns, (r: any) => [
      r.description, r.purpose, r._type, r.status,
      r.create_user ? `${r.create_user.first_name} ${r.create_user.last_name}` : '',
      r.categoryTree, r.category?.name,
    ].filter(Boolean).join(' ')),
    [allTransactions, search, filters, columns],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Stats row */}
      <div className="flex gap-[10px] flex-wrap" style={{ flexShrink: 0, marginBottom: 'var(--space-l1)' }}>
        <StatCard label={t('Balance')} value={stats.balance.toFixed(2)} color={stats.balance >= 0 ? 'var(--text-color)' : 'var(--color-danger)'} />
        <StatCard label={t('Incoming')} value={stats.incoming.toFixed(2)} color="var(--color-success)" />
        <StatCard label={t('Outgoing')} value={stats.outgoing.toFixed(2)} color="var(--color-danger)" />
        <StatCard label={t('Transactions')} value={String(stats.transactions)} />
      </div>

      {/* Toolbar */}
      <PageToolbar>
        <SearchFilter grow columns={columns} search={search} onSearch={setSearch} filters={filters} onFilters={setFilters} />
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="dt-btn-ghost"
          onClick={e => setMoreAnchor(e.currentTarget)}
          style={{ fontSize: 16, lineHeight: 1, padding: '0 6px' }}
          title="More options"
        >⋯</button>
        <Menu
          anchorEl={moreAnchor}
          open={Boolean(moreAnchor)}
          onClose={() => setMoreAnchor(null)}
          MenuListProps={{ dense: true, sx: { py: 0.5 } }}
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 160 } } }}
        >
          <MenuItem onClick={() => { setMoreAnchor(null); exportCsv(shownRows); }} sx={{ fontSize: 12.5, gap: 1.5, minHeight: 32 }}>
            <FontAwesomeIcon icon={faFileCsv} style={{ fontSize: 14, opacity: 0.7 }} /> Export CSV
          </MenuItem>
          <MenuItem onClick={() => { setMoreAnchor(null); handleExportZip(); }} disabled={zipping} sx={{ fontSize: 12.5, gap: 1.5, minHeight: 32 }}>
            <FontAwesomeIcon icon={faFileArrowDown} style={{ fontSize: 14, opacity: 0.7 }} /> {zipping ? 'Preparing…' : 'Export ZIP'}
          </MenuItem>
        </Menu>
        <div ref={toolbarSlot} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)' }} />
      </PageToolbar>

      {/* Table */}
      <div className="section-scroll">
        <DataTable
          rows={shownRows}
          getKey={(r: any) => `${r._type}-${r.id}`}
          columns={columns}
          filterable={false}
          defaultPageSize={20}
          storageKey="finance-overview-cols"
          toolbarRef={toolbarSlot}
        />
      </div>

      {/* Attachment viewer panel */}
      <SidePanel open={!!attachUrl} onClose={() => setAttachUrl(null)} width={560}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border2-new)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FontAwesomeIcon icon={faPaperclip} style={{ color: '#3b82f6' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachName}</span>
            <a
              href={attachUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              download
              style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none', fontWeight: 600 }}
            >
              ↓ {t('Download') || 'Download'}
            </a>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', background: 'var(--bg-top)' }}>
            {attachUrl && (
              /\.(jpg|jpeg|png|gif|webp)$/i.test(attachUrl) ? (
                <img src={attachUrl} alt={attachName} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 16 }} />
              ) : (
                <iframe
                  src={attachUrl}
                  title={attachName}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              )
            )}
          </div>
        </div>
      </SidePanel>
    </>
  );
};

export default FinanceOverview;
